import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSiteSettings } from '../../hooks/useContent';
import { resolveBrandLogoUrl } from '../../utils/siteSettingsJson';
import { dynamicAPI } from '../../services/api';
import { filterHeaderSearchItems } from '../../utils/headerSearchFilter';
import {
  ensureUpcomingSearchIndex,
  getCachedUpcomingSearchItems
} from '../../utils/upcomingSearchIndex';
import { popDocumentScrollLock, pushDocumentScrollLock } from '../../utils/documentScrollLock';
import './Header.css';

// Icon Components
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const MessageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const ShoppingBagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <path d="M16 10a4 4 0 0 1-8 0"></path>
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const LoginIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
    <polyline points="10 17 15 12 10 7"></polyline>
    <line x1="15" y1="12" x2="3" y2="12"></line>
  </svg>
);

const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const StarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
  </svg>
);

const InfoIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>
);

const BookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
  </svg>
);

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);

const Header = () => {
  const { settings } = useSiteSettings();
  const { user, isAuthenticated } = useAuth();
  const brandName = String(settings?.site_brand_name || 'INVINCIBLE').trim() || 'INVINCIBLE';
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchBlurTimerRef = useRef(null);
  const brandLogoSrc = resolveBrandLogoUrl(settings, 'header');

  useEffect(() => {
    (async () => {
      try {
        await ensureUpcomingSearchIndex(dynamicAPI);
      } catch {
        /* ignore — typing will retry */
      }
    })();
  }, []);

  // Check if a path is active
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  const closeMenu = () => setMenuOpen(false);

  const blogsNavActive =
    location.pathname.startsWith('/blogs') || location.pathname.startsWith('/blog');

  useEffect(() => {
    if (!menuOpen) return undefined;
    pushDocumentScrollLock();
    return () => popDocumentScrollLock();
  }, [menuOpen]);

  useEffect(() => {
    const term = String(searchInput || '').trim();
    if (term.length < 2) {
      setSearchSuggestions([]);
      setSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        if (!cancelled) setSearchLoading(true);
        await ensureUpcomingSearchIndex(dynamicAPI);
        if (cancelled) return;
        const fresh = getCachedUpcomingSearchItems();
        const filtered = filterHeaderSearchItems(fresh, term).slice(0, 8);
        setSearchSuggestions(
          filtered.map(({ slug, title, location, startDate }) => ({
            slug,
            title,
            location,
            startDate
          }))
        );
      } catch {
        if (!cancelled) setSearchSuggestions([]);
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 220);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [searchInput]);

  useEffect(() => {
    setShowSuggestions(false);
  }, [location.pathname, location.search]);

  const submitHeaderSearch = (rawValue, shouldCloseMenu = false) => {
    const term = String(rawValue || '').trim();
    if (shouldCloseMenu) closeMenu();
    setShowSuggestions(false);
    navigate(term ? `/events?search=${encodeURIComponent(term)}` : '/events');
  };

  const openSuggestion = (item, shouldCloseMenu = false) => {
    if (!item?.slug) return;
    if (shouldCloseMenu) closeMenu();
    setShowSuggestions(false);
    const url = item.startDate ? `/events/${item.slug}?date=${encodeURIComponent(item.startDate)}` : `/events/${item.slug}`;
    navigate(url);
  };

  const renderSuggestionDropdown = (isMobile = false) => {
    if (!showSuggestions) return null;
    const term = String(searchInput || '').trim();
    if (term.length < 2) return null;
    return (
      <div className="search-suggestions" role="listbox" aria-label="Search suggestions">
        {searchLoading ? (
          <div className="search-suggestion-empty">Searching...</div>
        ) : searchSuggestions.length > 0 ? (
          searchSuggestions.map((item) => (
            <button
              key={item.slug}
              type="button"
              className="search-suggestion-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => openSuggestion(item, isMobile)}
            >
              <span className="search-suggestion-title">{item.title}</span>
              {item.location ? <span className="search-suggestion-meta">{item.location}</span> : null}
            </button>
          ))
        ) : (
          <button
            type="button"
            className="search-suggestion-item"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => submitHeaderSearch(searchInput, isMobile)}
          >
            <span className="search-suggestion-title">Search for "{term}"</span>
            <span className="search-suggestion-meta">View all matching events</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      
      <header className="site-header">{/* Announcement Bar */}
      <div className="announcement-bar">
        <div className="announcement-bar-inner">
          <span className="announcement-bar-text">
            <strong>Winter Events are Live on Website!</strong>
            {' '}・{' '}
            <a
              href="https://invinciblengo.org/special-events/winter-events"
              target="_blank"
              rel="noopener noreferrer"
              className="announcement-bar-link"
            >
              Explore Now
              <svg className="announcement-bar-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="15 3 21 3 21 9" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
              </svg>
            </a>
          </span>
        </div>
      </div>
      {/* Top Utility Bar */}
      <div className="utility-bar">
        <div className="utility-container">
          <Link to="/" className="logo" onClick={closeMenu}>
            <img src={brandLogoSrc} alt={`${brandName} logo`} className="logo-image" />
          </Link>
          <div className="utility-links utility-links-desktop">
            <Link to="/careers" className={`utility-btn ${isActive('/careers') ? 'active' : ''}`}>
              <UserIcon />
              <span>Careers</span>
            </Link>
            <Link to="/contact" className={`utility-btn ${isActive('/contact') ? 'active' : ''}`}>
              <MessageIcon />
              <span>Get in Touch</span>
            </Link>
            <Link to="/store" className={`utility-btn ${isActive('/store') ? 'active' : ''}`}>
              <ShoppingBagIcon />
              <span>Store</span>
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard" className={`user-name-link ${isActive('/dashboard') || isActive('/profile') || isActive('/bookings') ? 'active' : ''}`}>
                <UserIcon />
                <span>{[user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Dashboard'}</span>
              </Link>
            ) : (
              <Link to="/login" className={`login-link ${isActive('/login') ? 'active' : ''}`}>
                <LoginIcon />
                <span>Login / Dashboard</span>
              </Link>
            )}
          </div>
          <form
            className="search-bar search-bar-desktop"
            onSubmit={(e) => {
              e.preventDefault();
              submitHeaderSearch(searchInput);
            }}
          >
            <button type="submit" className="search-submit-btn" aria-label="Search">
              <SearchIcon />
            </button>
            <input
              type="text"
              placeholder="Search Adventures, Camps, Offers..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                if (searchBlurTimerRef.current) window.clearTimeout(searchBlurTimerRef.current);
                searchBlurTimerRef.current = window.setTimeout(() => setShowSuggestions(false), 120);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitHeaderSearch(searchInput);
                }
              }}
            />
            {renderSuggestionDropdown(false)}
          </form>
          <button
            type="button"
            className="header-hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      <div className={`mobile-drawer ${menuOpen ? 'mobile-drawer-open' : ''}`} aria-hidden={!menuOpen}>
        <div className="mobile-drawer-backdrop" onClick={closeMenu} />
        <div className="mobile-drawer-content">
          <form
            className="mobile-drawer-search"
            onSubmit={(e) => {
              e.preventDefault();
              submitHeaderSearch(searchInput, true);
            }}
          >
            <button type="submit" className="search-submit-btn" aria-label="Search">
              <SearchIcon />
            </button>
            <input
              type="text"
              placeholder="Search Adventures, Camps, Offers..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => {
                if (searchBlurTimerRef.current) window.clearTimeout(searchBlurTimerRef.current);
                searchBlurTimerRef.current = window.setTimeout(() => setShowSuggestions(false), 120);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitHeaderSearch(searchInput, true);
                }
              }}
            />
            {renderSuggestionDropdown(true)}
          </form>
          <nav className="mobile-drawer-nav" aria-label="Main">
            <Link
              to="/events"
              className={`nav-link ${isActive('/events') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <CalendarIcon />
              <span>Upcoming Events</span>
            </Link>
            <Link
              to="/expeditions"
              className={`nav-link ${isActive('/expeditions') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <StarIcon />
              <span>Special Events</span>
            </Link>
            <Link
              to="/about"
              className={`nav-link ${isActive('/about') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <InfoIcon />
              <span>About Us</span>
            </Link>
            <Link
              to="/blogs"
              className={`nav-link ${blogsNavActive ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <BookIcon />
              <span>Blogs</span>
            </Link>
            <Link
              to="/team"
              className={`nav-link ${isActive('/team') ? 'active' : ''}`}
              onClick={closeMenu}
            >
              <UsersIcon />
              <span>Team</span>
            </Link>
          </nav>
          <div className="mobile-drawer-links mobile-drawer-links-after-nav">
            <Link to="/careers" className={`utility-btn ${isActive('/careers') ? 'active' : ''}`} onClick={closeMenu}>
              <UserIcon />
              <span>Careers</span>
            </Link>
            <Link to="/contact" className={`utility-btn ${isActive('/contact') ? 'active' : ''}`} onClick={closeMenu}>
              <MessageIcon />
              <span>Get in Touch</span>
            </Link>
            <Link to="/store" className={`utility-btn ${isActive('/store') ? 'active' : ''}`} onClick={closeMenu}>
              <ShoppingBagIcon />
              <span>Store</span>
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard" className={`user-name-link ${isActive('/dashboard') || isActive('/profile') || isActive('/bookings') ? 'active' : ''}`} onClick={closeMenu}>
                <UserIcon />
                <span>{[user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Dashboard'}</span>
              </Link>
            ) : (
              <Link to="/login" className={`login-link ${isActive('/login') ? 'active' : ''}`} onClick={closeMenu}>
                <LoginIcon />
                <span>Login / Dashboard</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Navigation Bar (desktop) */}
      <nav className="main-nav main-nav-desktop">
        <div className="nav-container">
          <Link to="/events" className={`nav-link ${isActive('/events') ? 'active' : ''}`}>
            <CalendarIcon />
            <span>Upcoming Events</span>
          </Link>
          <Link to="/expeditions" className={`nav-link ${isActive('/expeditions') ? 'active' : ''}`}>
            <StarIcon />
            <span>Special Events</span>
          </Link>
          <Link to="/about" className={`nav-link ${isActive('/about') ? 'active' : ''}`}>
            <InfoIcon />
            <span>About Us</span>
          </Link>
          <Link to="/blogs" className={`nav-link ${blogsNavActive ? 'active' : ''}`}>
            <BookIcon />
            <span>Blogs</span>
          </Link>
          <Link to="/team" className={`nav-link ${isActive('/team') ? 'active' : ''}`}>
            <UsersIcon />
            <span>Team</span>
          </Link>
        </div>
      </nav>
      </header>
    </>
  );
};

export default Header;
